// Analytics: Delivers events to analytics when interesting behaviors occur

export interface Logger {
  log(...args: unknown[]): void;
  info(...args: unknown[]): void;
  warn(...args: unknown[]): void;
  error(...args: unknown[]): void;
  time(label?: string): void;
  timeEnd(label?: string): void;
}

interface AnalyticsGlobal {
  webData: { toolName: string };
  setWebEvent: (name: string, options: { toolName?: string; event?: string; payload?: string }) => void;
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace globalThis {
    // eslint-disable-next-line no-var
    var AnalyticsAL: AnalyticsGlobal | undefined;
  }
}

interface QueuedEvent {
  tool: string;
  evt: string;
  data?: string;
}
//------------------------ Local non-class functions ---------------------

const loadScript = (source: string, beforeEl?: Element, async = true, defer = true) => {
  // eslint-disable-next-line no-undef
  return new Promise((resolve, reject) => {
    let script: HTMLScriptElement | undefined = document.createElement('script');
    const scripts = document.getElementsByTagName('script');
    const lastScript = scripts && scripts.length ? scripts[scripts.length - 1] : undefined;
    const prior = beforeEl || lastScript?.nextElementSibling;

    script.async = async;
    script.defer = defer;

    function onloadHander(this: GlobalEventHandlers, event: Event): void {
      if (script) {
        script.onload = null;
        script = undefined;
      }
      event instanceof AbortSignal ? reject() : resolve('');
    }

    script.onload = onloadHander;

    script.src = source;
    if (prior) {
      prior.parentNode?.insertBefore(script, prior);
    } else {
      document.getElementsByTagName('body')[0]?.append(script);
    }
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const poll = function (fn: () => any, interval: number, retries: number) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const checkCondition = function (resolve: (result: any) => void, reject: (e: Error) => void) {
    // If the condition is met, we're done!
    const result = fn();
    if (result) {
      resolve(result);
    }
    // If the condition isn't met but the timeout hasn't elapsed, go again
    else if (--retries > 0) {
      setTimeout(checkCondition, interval, resolve, reject);
    }
    // Didn't match and too much time, reject!
    else {
      reject(new Error('timed out for ' + fn));
    }
  };

  // eslint-disable-next-line no-undef
  return new Promise(checkCondition);
};

const supplyDummySession = function (toolname: string, logger: Logger, message?: string) {
  // Supply placeholder for dummmy analytics for remainder of session
  globalThis.AnalyticsAL = {
    setWebEvent: function () {
      if (message) {
        logger.info(message);
      }
    },
    webData: { toolName: toolname },
  };
};

const releaseDummySessionResources = function () {
  globalThis.AnalyticsAL = undefined;
};

const analyticsLoaded = function () {
  return typeof globalThis.AnalyticsAL !== 'undefined' && globalThis.AnalyticsAL;
};

const CONSOLE_LOGGER: Logger = {
  error(...args) {
    console.error(...args);
  }, // eslint-disable-line no-console
  warn(...args) {
    console.warn(...args);
  }, // eslint-disable-line no-console
  info(...args) {
    console.info(...args);
  }, // eslint-disable-line no-console
  log(...args) {
    console.error(...args);
  }, // eslint-disable-line no-console
  time(...args) {
    console.time(...args);
  }, // eslint-disable-line no-console
  timeEnd(...args) {
    console.timeEnd(...args);
  }, // eslint-disable-line no-console
};
const INTERVAL = 200; //ms
const RETRIES = 30;
// Analytics states
export const LOADING = 0;
export const ACTIVE = 1;
export const ABANDONED = -1;

//-----------------------------Analytics class definition---------------------

export default class Analytics {
  toolname = '';
  analyticsUrl = '';
  logSuppressions = false;
  usesDummySession = false;
  state = LOADING;
  queue: Array<QueuedEvent> = [];
  logger: Logger = CONSOLE_LOGGER;
  start(
    toolname: string,
    analyticsUrl: string,
    isSSR: boolean,
    isTesting: boolean,
    logSuppressions: boolean,
    logger?: Logger,
  ): void {
    this.toolname = toolname;
    this.logger = logger || this.logger;
    this.logSuppressions = logSuppressions;
    if (isSSR) {
      this.logger.info('Analytics turned off for SSR');
      this.usesDummySession = true;
      supplyDummySession(this.toolname, this.logger);
      this.state = ABANDONED;
      this._trackSessionStart();
    } else if (isTesting) {
      this.logger.info('Analytics turned off in test automation');
      this.usesDummySession = true;
      supplyDummySession(this.toolname, this.logger);
      this.state = ABANDONED;
      this._trackSessionStart();
    } else {
      this.logger.time('analyticsLoadTime');
      loadScript(analyticsUrl)
        .then(() => {
          // Execution of the Analytics URL from Adobe defines a global AnalyticsAL object ... eventually ... in theory
          poll(analyticsLoaded, INTERVAL, RETRIES)
            .then(() => {
              // if statement to satisfy TypeScript
              if (globalThis.AnalyticsAL) {
                this.logger.timeEnd('analyticsLoadTime');
                this.state = ACTIVE;
                globalThis.AnalyticsAL.webData.toolName = this.toolname;
                this._trackSessionStart();
              }
            })
            .catch(() => {
              this.logger.timeEnd('analyticsLoadTime');
              this.state = ABANDONED;
              this.logger.warn(
                `Analytics didn't start after a timeout of roughly ${(INTERVAL * RETRIES) / 1000} seconds.`,
              );
              this.usesDummySession = true;
              supplyDummySession(
                this.toolname,
                this.logger,
                'Analytics event not delivered - analytics startup timed out',
              );
              this._trackSessionStart();
            });
        })
        .catch((error) => {
          this.logger.timeEnd('analyticsLoadTime');
          this.state = ABANDONED;
          this.logger.warn(`Analytics failed to load for ${location.origin}.`, error);
          this.usesDummySession = true;
          supplyDummySession(
            this.toolname,
            this.logger,
            'Analytics event not delivered - analytics URL failed to load',
          );
          this._trackSessionStart();
        });
    }
  }
  stop() {
    if (this.usesDummySession) {
      releaseDummySessionResources();
    }
    this.queue = [];
    this.state = LOADING;
  }
  _trackSessionStart() {
    // Track session start
    // if statement to satisfy TypeScript
    if (globalThis.AnalyticsAL) {
      globalThis.AnalyticsAL.setWebEvent('toolStart', { toolName: this.toolname });
      this.logger.info(`ADOBEDTM: ${this.toolname} session start`);
      for (let q = 0, qlen = this.queue.length; q < qlen; q++) {
        const item = this.queue[q];
        if (item) {
          this.setWebEvent(item.tool, item.evt, item.data);
        }
      }
    }
    this.queue = [];
  }
  setWebEvent(toolName: string, eventName: string, eventData?: string) {
    if (this.state == LOADING) {
      this.queue.push({ tool: toolName, evt: eventName, data: eventData });
    } else {
      try {
        if (globalThis.AnalyticsAL) {
          globalThis.AnalyticsAL.setWebEvent('payload', { event: eventName, payload: eventData });
          if (!this.usesDummySession || this.logSuppressions) {
            this.logger.info(`ADOBEDTM: ${this.toolname} [${eventName}]`, eventData);
          }
        }
      } catch (e) {
        this.logger.error(`setWebEvent or subsequent logging failed unexpectedly for ${eventName}`, eventData);
      }
    }
  }
}
